import { atom } from 'nanostores';
import { EventInput, EventSourceInput } from '@fullcalendar/core';
import { Play, Syncable } from '../types';
import { client } from '../apiClient';
import { $isOnline } from './app';

export interface PlannerEvent extends EventInput {
  id: string;
  planType: 'training' | 'match' | 'other';
  description?: string;
  note?: string;

  // Training-specific fields
  trainingDuration?: number; // duration in minutes
  trainingTypes?: string[];

  // Match-specific fields
  competitionType?: string;
  matchPlan?: string;
  players?: Player[];
  plays?: Play[];
}

export type Player = {
  id: string;
  name: string;
  number: number;
  flag: string;
  role?: 'str' | 'sub';
};

export const $selectedPreviewEvent = atom<PlannerEvent | null>(null);
export const $selectedEvent = atom<PlannerEvent | null>(null);
export const $plannerModalIsOpen = atom<boolean>(false);
export const $selectedDetailsEvent = atom<PlannerEvent | null>(null);
export const $players = atom<Syncable<Player>[]>([]);
export const $playersIsLoading = atom<boolean>(true);

export const $events = atom<Syncable<PlannerEvent>[]>([]);
export const $eventsIsLoading = atom<boolean>(true);

// --- Events cache layer ---

async function fetchEventsFromCache() {
  let allEvents: Syncable<PlannerEvent>[] = [];
  try {
    const eventCache = await caches.open('plannerEvents');
    if (eventCache) {
      const response = await eventCache.match('events');
      if (response) {
        const data = await response.json();
        allEvents = data as Syncable<PlannerEvent>[];
      } else {
        console.log('noresponse', 'plannerEvents');
      }
    }
  } catch (e) {
    console.error('fetchEventsFromCache::error', e);
  }
  return allEvents;
}

function saveEventsToCache(events: Syncable<PlannerEvent>[]) {
  const eventsCache = caches.open('plannerEvents');
  if (eventsCache) {
    eventsCache
      .then(async (cache) => {
        const blob = new Blob([JSON.stringify(events)], {
          type: 'application/json',
        });
        const response = new Response(blob);
        await cache.put('events', response);
        console.log('planner events saved to cache');
      })
      .catch(() => {
        console.error('Failed to save planner events to cache');
      });
  }
}

// --- Events backend fetch ---

function parseEventFromBackend(data: any): Syncable<PlannerEvent> {
  return {
    ...data,
    players: data.players ? JSON.parse(data.players) : undefined,
    plays: data.plays ? JSON.parse(data.plays) : undefined,
    trainingTypes: data.trainingType
      ? (typeof data.trainingType === 'string' && data.trainingType.startsWith('['))
        ? JSON.parse(data.trainingType)
        : [data.trainingType]
      : undefined,
  };
}

async function fetchEvents(): Promise<Array<Syncable<PlannerEvent>>> {
  let allEvents: Syncable<PlannerEvent>[] = [];
  let nextToken: string | undefined | null = undefined;
  let hasError: any[] | undefined = undefined;

  if (!$isOnline.get()) {
    return await fetchEventsFromCache();
  }

  do {
    const {
      data: events,
      errors,
      nextToken: nT,
    } = await client.models.PlannerEvent.list({ nextToken, limit: 150 });
    if (!events && errors && errors.length > 0) {
      hasError = errors;
    }
    if (events && events.length > 0) {
      allEvents = allEvents.concat(events.map(parseEventFromBackend));
    }
    nextToken = nT;
  } while (nextToken && !hasError);

  if (hasError) {
    return await fetchEventsFromCache();
  }

  saveEventsToCache(allEvents);

  return allEvents;
}

// --- Events draft queue ---

async function addEventToSyncQueue(event: Syncable<PlannerEvent>) {
  console.log('addEventToSyncQueue', event);
  const currentDraftCache = caches.open('draftPlannerEvents');
  if (currentDraftCache) {
    currentDraftCache
      .then((cache) => {
        const blob = new Blob([JSON.stringify(event)], {
          type: 'application/json',
        });
        const response = new Response(blob);
        cache.put(`event-${event.id}`, response);
      })
      .catch(() => {
        throw new Error('Failed to save event to sync queue', {
          cause: 'Failed to open cache',
        });
      });
  }
}

const removeEventFromSyncQueue = async (event: Syncable<PlannerEvent>) => {
  const currentDraftCache = caches.open('draftPlannerEvents');
  currentDraftCache
    .then((cache) => {
      cache.delete(`event-${event.id}`);
    })
    .catch(() => {
      throw new Error('Failed to remove event from sync queue', {
        cause: 'Failed to open cache',
      });
    });
};

async function getUnsyncedEvents() {
  let events: Syncable<PlannerEvent>[] = [];
  const currentDraftCache = await caches.open('draftPlannerEvents');
  try {
    const keys = await currentDraftCache.keys();
    const promises = keys.map(async (key) => {
      const response = await currentDraftCache.match(key);
      if (response) {
        return response.json();
      }
    });
    const data = await Promise.all(promises);
    events = data.filter((p) => p !== null) as Syncable<PlannerEvent>[];
  } catch (e) {
    console.error('Failed to get keys from draftPlannerEvents cache', {
      cause: e,
    });
  }
  console.log('getUnsyncedEvents', events);
  return events;
}

// --- Events CRUD ---

function buildEventPayload(event: Syncable<PlannerEvent>) {
  return {
    id: event.id as string,
    title: event.title as string,
    start:
      event.start instanceof Date
        ? event.start.toISOString()
        : String(event.start),
    end: event.end
      ? event.end instanceof Date
        ? event.end.toISOString()
        : String(event.end)
      : undefined,
    allDay: event.allDay,
    color: event.color,
    planType: event.planType,
    description: event.description,
    note: event.note,
    trainingDuration: event.trainingDuration,
    trainingType: event.trainingTypes ? JSON.stringify(event.trainingTypes) : undefined,
    competitionType: event.competitionType,
    matchPlan: event.matchPlan,
    players: event.players ? JSON.stringify(event.players) : undefined,
    plays: event.plays ? JSON.stringify(event.plays) : undefined,
  };
}

export async function saveEvent(
  event: Syncable<PlannerEvent>,
  fromSync?: boolean,
): Promise<Syncable<PlannerEvent>> {
  if (!$isOnline.get()) {
    if (fromSync) {
      return event;
    }
    await addEventToSyncQueue(event);
    const _events = $events.get();
    const index = _events.findIndex((e) => e.id === event.id);
    if (index !== -1) {
      const updatedEvents = [..._events];
      updatedEvents[index] = event;
      $events.set(updatedEvents);
    } else {
      $events.set([..._events, event]);
    }
    return event;
  }

  const payload = buildEventPayload(event);

  if (event.isDraft) {
    const { data, errors } = await client.models.PlannerEvent.create(payload);
    if (data && data.id === event.id) {
      event.synced = true;
      event.isDraft = false;
      void removeEventFromSyncQueue(event);
    }

    if (errors && errors.length > 0 && !fromSync) {
      await addEventToSyncQueue(event);
    }
    const _events = $events.get();
    const index = _events.findIndex((e) => e.id === event.id);
    if (index !== -1) {
      const updatedEvents = [..._events];
      updatedEvents[index] = event;
      $events.set(updatedEvents);
    } else {
      $events.set([..._events, event]);
    }
  } else {
    const { data, errors } = await client.models.PlannerEvent.update({
      ...payload,
      id: event.id!,
    });
    if (data && data.id === event.id) {
      event.synced = true;
      void removeEventFromSyncQueue(event);
    }
    if (errors && errors.length > 0 && !fromSync) {
      await addEventToSyncQueue(event);
    }
    const _events = $events.get();
    const index = _events.findIndex((e) => e.id === event.id);
    if (index !== -1) {
      const updatedEvents = [..._events];
      updatedEvents[index] = event;
      $events.set(updatedEvents);
    }
  }

  return event;
}

export async function removeEvent(id: string) {
  if ($isOnline.get()) {
    try {
      await client.models.PlannerEvent.delete({ id });
    } catch (e) {
      console.error('removeEvent::error', e);
    }
  }
  $events.set($events.get().filter((e) => e.id !== id));
}

// --- Events fetch + merge ---

export async function getEvents() {
  $eventsIsLoading.set(true);
  const events = await fetchEvents();
  const unsyncedEvents = await getUnsyncedEvents();
  const filteredEvents = events.filter(
    (e) => !unsyncedEvents.some((ue) => ue.id === e.id),
  );
  $events.set([...filteredEvents, ...unsyncedEvents]);
  $eventsIsLoading.set(false);
  return [...filteredEvents, ...unsyncedEvents];
}

// --- Events sync ---

export async function syncPlannerEvents() {
  try {
    const draftCache = await getUnsyncedEvents();
    if (draftCache.length > 0) {
      console.log('syncPlannerEvents', 'draftCache:', draftCache);
      await Promise.all(
        draftCache.map(async (event) => {
          return saveEvent(event, true);
        }),
      );
    }
  } catch (e) {}
}

// Player CRUD operations
export const $managePlayersModalIsOpen = atom<boolean>(false);

// --- Players cache layer ---

async function fetchPlayersFromCache() {
  let allPlayers: Syncable<Player>[] = [];
  try {
    const playerCache = await caches.open('plannerPlayers');
    if (playerCache) {
      const response = await playerCache.match('players');
      if (response) {
        const data = await response.json();
        allPlayers = data as Syncable<Player>[];
      } else {
        console.log('noresponse', 'plannerPlayers');
      }
    }
  } catch (e) {
    console.error('fetchPlayersFromCache::error', e);
  }
  return allPlayers;
}

async function fetchPlayers(): Promise<Array<Syncable<Player>>> {
  let allPlayers: Syncable<Player>[] = [];
  let nextToken: string | undefined | null = undefined;
  let hasError: any[] | undefined = undefined;

  if (!$isOnline.get()) {
    return await fetchPlayersFromCache();
  }

  do {
    const {
      data: players,
      errors,
      nextToken: nT,
    } = await client.models.Player.list({ nextToken, limit: 150 });
    if (!players && errors && errors.length > 0) {
      hasError = errors;
    }
    if (players && players.length > 0) {
      allPlayers = allPlayers.concat(players as Player[]);
    }
    nextToken = nT;
  } while (nextToken && !hasError);

  if (hasError) {
    return await fetchPlayersFromCache();
  }

  const playersCache = caches.open('plannerPlayers');
  if (playersCache) {
    playersCache
      .then(async (cache) => {
        const blob = new Blob([JSON.stringify(allPlayers)], {
          type: 'application/json',
        });
        const response = new Response(blob);
        await cache.put('players', response);
        console.log('plannerPlayers saved to cache');
      })
      .catch(() => {
        console.error('Failed to save plannerPlayers to cache');
      });
  }

  return allPlayers;
}

// --- Players draft queue ---

async function addPlayerToSyncQueue(player: Syncable<Player>) {
  console.log('addPlayerToSyncQueue', player);
  const currentDraftCache = caches.open('draftPlannerPlayers');
  if (currentDraftCache) {
    currentDraftCache
      .then((cache) => {
        const blob = new Blob([JSON.stringify(player)], {
          type: 'application/json',
        });
        const response = new Response(blob);
        cache.put(`player-${player.id}`, response);
      })
      .catch(() => {
        throw new Error('Failed to save player to sync queue', {
          cause: 'Failed to open cache',
        });
      });
  }
}

const removePlayerFromSyncQueue = async (player: Syncable<Player>) => {
  const currentDraftCache = caches.open('draftPlannerPlayers');
  currentDraftCache
    .then((cache) => {
      cache.delete(`player-${player.id}`);
    })
    .catch(() => {
      throw new Error('Failed to remove player from sync queue', {
        cause: 'Failed to open cache',
      });
    });
};

async function getUnsyncedPlayers() {
  let players: Syncable<Player>[] = [];
  const currentDraftCache = await caches.open('draftPlannerPlayers');
  try {
    const keys = await currentDraftCache.keys();
    const promises = keys.map(async (key) => {
      const response = await currentDraftCache.match(key);
      if (response) {
        return response.json();
      }
    });
    const data = await Promise.all(promises);
    players = data.filter((p) => p !== null) as Syncable<Player>[];
  } catch (e) {
    console.error('Failed to get keys from draftPlannerPlayers cache', {
      cause: e,
    });
  }
  return players;
}

// --- Players CRUD ---

export async function savePlayer(
  player: Syncable<Player>,
  fromSync?: boolean,
): Promise<Syncable<Player>> {
  if (!$isOnline.get()) {
    if (fromSync) {
      return player;
    }
    await addPlayerToSyncQueue(player);
    const _players = $players.get();
    const index = _players.findIndex((p) => p.id === player.id);
    if (index !== -1) {
      const updatedPlayers = [..._players];
      updatedPlayers[index] = player;
      $players.set(updatedPlayers);
    } else {
      $players.set([..._players, player]);
    }
    return player;
  }
  const { isDraft, synced, ...payload } = player;

  if (player.isDraft) {
    const { data, errors } = await client.models.Player.create(payload);
    if (data && data.id === player.id) {
      player.synced = true;
      player.isDraft = false;
      void removePlayerFromSyncQueue(player);
    }

    if (errors && errors.length > 0 && !fromSync) {
      await addPlayerToSyncQueue(player);
    }
    const _players = $players.get();
    const index = _players.findIndex((p) => p.id === player.id);
    if (index !== -1) {
      const updatedPlayers = [..._players];
      updatedPlayers[index] = player;
      $players.set(updatedPlayers);
    } else {
      $players.set([..._players, player]);
    }
  } else {
    const { data, errors } = await client.models.Player.update({
      ...payload,
      id: player.id!,
    });
    if (data && data.id === player.id) {
      player.synced = true;
      void removePlayerFromSyncQueue(player);
    }
    if (errors && errors.length > 0 && !fromSync) {
      await addPlayerToSyncQueue(player);
    }
    const _players = $players.get();
    const index = _players.findIndex((p) => p.id === player.id);
    if (index !== -1) {
      const updatedPlayers = [..._players];
      updatedPlayers[index] = player;
      $players.set(updatedPlayers);
    }
  }

  return player;
}

export async function removePlayer(id: string) {
  if ($isOnline.get()) {
    await client.models.Player.delete({ id });
  }
  $players.set($players.get().filter((p) => p.id !== id));
}

export function addPlayer(player: Player) {
  void savePlayer({ ...player, isDraft: true });
}

export function updatePlayer(updatedPlayer: Player) {
  void savePlayer(updatedPlayer);
}

// --- Players fetch + merge ---

export async function getPlayers() {
  $playersIsLoading.set(true);
  const players = await fetchPlayers();
  const unsyncedPlayers = await getUnsyncedPlayers();
  const filteredPlayers = players.filter(
    (p) => !unsyncedPlayers.some((up) => up.id === p.id),
  );
  $players.set([...filteredPlayers, ...unsyncedPlayers]);
  $playersIsLoading.set(false);
  return [...players, ...unsyncedPlayers];
}

// --- Players sync ---

export async function syncPlannerPlayers() {
  try {
    const draftCache = await getUnsyncedPlayers();
    if (draftCache.length > 0) {
      console.log('syncPlannerPlayers', 'draftCache:', draftCache);
      await Promise.all(
        draftCache.map(async (player) => {
          return savePlayer(player, true);
        }),
      );
    }
  } catch (e) {}
}

// TrainingType CRUD operations

export type TrainingType = {
  id: string;
  name: string;
};

export const $trainingTypes = atom<Syncable<TrainingType>[]>([]);
export const $trainingTypesIsLoading = atom<boolean>(true);
export const $manageTrainingTypesModalIsOpen = atom<boolean>(false);

// --- TrainingTypes cache layer ---

async function fetchTrainingTypesFromCache() {
  let allTrainingTypes: Syncable<TrainingType>[] = [];
  try {
    const trainingTypeCache = await caches.open('plannerTrainingTypes');
    if (trainingTypeCache) {
      const response = await trainingTypeCache.match('trainingTypes');
      if (response) {
        const data = await response.json();
        allTrainingTypes = data as Syncable<TrainingType>[];
      } else {
        console.log('noresponse', 'plannerTrainingTypes');
      }
    }
  } catch (e) {
    console.error('fetchTrainingTypesFromCache::error', e);
  }
  return allTrainingTypes;
}

async function fetchTrainingTypes(): Promise<Array<Syncable<TrainingType>>> {
  let allTrainingTypes: Syncable<TrainingType>[] = [];
  let nextToken: string | undefined | null = undefined;
  let hasError: any[] | undefined = undefined;

  if (!$isOnline.get()) {
    return await fetchTrainingTypesFromCache();
  }

  do {
    const {
      data: trainingTypes,
      errors,
      nextToken: nT,
    } = await client.models.TrainingType.list({ nextToken, limit: 150 });
    if (!trainingTypes && errors && errors.length > 0) {
      hasError = errors;
    }
    if (trainingTypes && trainingTypes.length > 0) {
      allTrainingTypes = allTrainingTypes.concat(trainingTypes as TrainingType[]);
    }
    nextToken = nT;
  } while (nextToken && !hasError);

  if (hasError) {
    return await fetchTrainingTypesFromCache();
  }

  const trainingTypesCache = caches.open('plannerTrainingTypes');
  if (trainingTypesCache) {
    trainingTypesCache
      .then(async (cache) => {
        const blob = new Blob([JSON.stringify(allTrainingTypes)], {
          type: 'application/json',
        });
        const response = new Response(blob);
        await cache.put('trainingTypes', response);
        console.log('plannerTrainingTypes saved to cache');
      })
      .catch(() => {
        console.error('Failed to save plannerTrainingTypes to cache');
      });
  }

  return allTrainingTypes;
}

// --- TrainingTypes draft queue ---

async function addTrainingTypeToSyncQueue(trainingType: Syncable<TrainingType>) {
  console.log('addTrainingTypeToSyncQueue', trainingType);
  const currentDraftCache = caches.open('draftPlannerTrainingTypes');
  if (currentDraftCache) {
    currentDraftCache
      .then((cache) => {
        const blob = new Blob([JSON.stringify(trainingType)], {
          type: 'application/json',
        });
        const response = new Response(blob);
        cache.put(`trainingType-${trainingType.id}`, response);
      })
      .catch(() => {
        throw new Error('Failed to save trainingType to sync queue', {
          cause: 'Failed to open cache',
        });
      });
  }
}

const removeTrainingTypeFromSyncQueue = async (trainingType: Syncable<TrainingType>) => {
  const currentDraftCache = caches.open('draftPlannerTrainingTypes');
  currentDraftCache
    .then((cache) => {
      cache.delete(`trainingType-${trainingType.id}`);
    })
    .catch(() => {
      throw new Error('Failed to remove trainingType from sync queue', {
        cause: 'Failed to open cache',
      });
    });
};

async function getUnsyncedTrainingTypes() {
  let trainingTypes: Syncable<TrainingType>[] = [];
  const currentDraftCache = await caches.open('draftPlannerTrainingTypes');
  try {
    const keys = await currentDraftCache.keys();
    const promises = keys.map(async (key) => {
      const response = await currentDraftCache.match(key);
      if (response) {
        return response.json();
      }
    });
    const data = await Promise.all(promises);
    trainingTypes = data.filter((p) => p !== null) as Syncable<TrainingType>[];
  } catch (e) {
    console.error('Failed to get keys from draftPlannerTrainingTypes cache', {
      cause: e,
    });
  }
  return trainingTypes;
}

// --- TrainingTypes CRUD ---

export async function saveTrainingType(
  trainingType: Syncable<TrainingType>,
  fromSync?: boolean,
): Promise<Syncable<TrainingType>> {
  if (!$isOnline.get()) {
    if (fromSync) {
      return trainingType;
    }
    await addTrainingTypeToSyncQueue(trainingType);
    const _trainingTypes = $trainingTypes.get();
    const index = _trainingTypes.findIndex((t) => t.id === trainingType.id);
    if (index !== -1) {
      const updatedTrainingTypes = [..._trainingTypes];
      updatedTrainingTypes[index] = trainingType;
      $trainingTypes.set(updatedTrainingTypes);
    } else {
      $trainingTypes.set([..._trainingTypes, trainingType]);
    }
    return trainingType;
  }
  const { isDraft, synced, ...payload } = trainingType;

  if (trainingType.isDraft) {
    const { data, errors } = await client.models.TrainingType.create(payload);
    if (data && data.id === trainingType.id) {
      trainingType.synced = true;
      trainingType.isDraft = false;
      void removeTrainingTypeFromSyncQueue(trainingType);
    }

    if (errors && errors.length > 0 && !fromSync) {
      await addTrainingTypeToSyncQueue(trainingType);
    }
    const _trainingTypes = $trainingTypes.get();
    const index = _trainingTypes.findIndex((t) => t.id === trainingType.id);
    if (index !== -1) {
      const updatedTrainingTypes = [..._trainingTypes];
      updatedTrainingTypes[index] = trainingType;
      $trainingTypes.set(updatedTrainingTypes);
    } else {
      $trainingTypes.set([..._trainingTypes, trainingType]);
    }
  } else {
    const { data, errors } = await client.models.TrainingType.update({
      ...payload,
      id: trainingType.id!,
    });
    if (data && data.id === trainingType.id) {
      trainingType.synced = true;
      void removeTrainingTypeFromSyncQueue(trainingType);
    }
    if (errors && errors.length > 0 && !fromSync) {
      await addTrainingTypeToSyncQueue(trainingType);
    }
    const _trainingTypes = $trainingTypes.get();
    const index = _trainingTypes.findIndex((t) => t.id === trainingType.id);
    if (index !== -1) {
      const updatedTrainingTypes = [..._trainingTypes];
      updatedTrainingTypes[index] = trainingType;
      $trainingTypes.set(updatedTrainingTypes);
    }
  }

  return trainingType;
}

export async function removeTrainingType(id: string) {
  if ($isOnline.get()) {
    await client.models.TrainingType.delete({ id });
  }
  $trainingTypes.set($trainingTypes.get().filter((t) => t.id !== id));
}

export function addTrainingType(trainingType: TrainingType) {
  void saveTrainingType({ ...trainingType, isDraft: true });
}

export function updateTrainingType(updatedTrainingType: TrainingType) {
  void saveTrainingType(updatedTrainingType);
}

// --- TrainingTypes fetch + merge ---

export async function getTrainingTypes() {
  $trainingTypesIsLoading.set(true);
  const trainingTypes = await fetchTrainingTypes();
  const unsyncedTrainingTypes = await getUnsyncedTrainingTypes();
  const filteredTrainingTypes = trainingTypes.filter(
    (t) => !unsyncedTrainingTypes.some((ut) => ut.id === t.id),
  );
  $trainingTypes.set([...filteredTrainingTypes, ...unsyncedTrainingTypes]);
  $trainingTypesIsLoading.set(false);
  return [...filteredTrainingTypes, ...unsyncedTrainingTypes];
}

// --- TrainingTypes sync ---

export async function syncPlannerTrainingTypes() {
  try {
    const draftCache = await getUnsyncedTrainingTypes();
    if (draftCache.length > 0) {
      console.log('syncPlannerTrainingTypes', 'draftCache:', draftCache);
      await Promise.all(
        draftCache.map(async (trainingType) => {
          return saveTrainingType(trainingType, true);
        }),
      );
    }
  } catch (e) {}
}

// CompetitionType CRUD operations

export type CompetitionType = {
  id: string;
  name: string;
};

export const $competitionTypes = atom<Syncable<CompetitionType>[]>([]);
export const $competitionTypesIsLoading = atom<boolean>(true);
export const $manageCompetitionTypesModalIsOpen = atom<boolean>(false);

// --- CompetitionTypes cache layer ---

async function fetchCompetitionTypesFromCache() {
  let allCompetitionTypes: Syncable<CompetitionType>[] = [];
  try {
    const competitionTypeCache = await caches.open('plannerCompetitionTypes');
    if (competitionTypeCache) {
      const response = await competitionTypeCache.match('competitionTypes');
      if (response) {
        const data = await response.json();
        allCompetitionTypes = data as Syncable<CompetitionType>[];
      } else {
        console.log('noresponse', 'plannerCompetitionTypes');
      }
    }
  } catch (e) {
    console.error('fetchCompetitionTypesFromCache::error', e);
  }
  return allCompetitionTypes;
}

async function fetchCompetitionTypes(): Promise<Array<Syncable<CompetitionType>>> {
  let allCompetitionTypes: Syncable<CompetitionType>[] = [];
  let nextToken: string | undefined | null = undefined;
  let hasError: any[] | undefined = undefined;

  if (!$isOnline.get()) {
    return await fetchCompetitionTypesFromCache();
  }

  do {
    const {
      data: competitionTypes,
      errors,
      nextToken: nT,
    } = await client.models.CompetitionType.list({ nextToken, limit: 150 });
    if (!competitionTypes && errors && errors.length > 0) {
      hasError = errors;
    }
    if (competitionTypes && competitionTypes.length > 0) {
      allCompetitionTypes = allCompetitionTypes.concat(competitionTypes as CompetitionType[]);
    }
    nextToken = nT;
  } while (nextToken && !hasError);

  if (hasError) {
    return await fetchCompetitionTypesFromCache();
  }

  const competitionTypesCache = caches.open('plannerCompetitionTypes');
  if (competitionTypesCache) {
    competitionTypesCache
      .then(async (cache) => {
        const blob = new Blob([JSON.stringify(allCompetitionTypes)], {
          type: 'application/json',
        });
        const response = new Response(blob);
        await cache.put('competitionTypes', response);
        console.log('plannerCompetitionTypes saved to cache');
      })
      .catch(() => {
        console.error('Failed to save plannerCompetitionTypes to cache');
      });
  }

  return allCompetitionTypes;
}

// --- CompetitionTypes draft queue ---

async function addCompetitionTypeToSyncQueue(competitionType: Syncable<CompetitionType>) {
  console.log('addCompetitionTypeToSyncQueue', competitionType);
  const currentDraftCache = caches.open('draftPlannerCompetitionTypes');
  if (currentDraftCache) {
    currentDraftCache
      .then((cache) => {
        const blob = new Blob([JSON.stringify(competitionType)], {
          type: 'application/json',
        });
        const response = new Response(blob);
        cache.put(`competitionType-${competitionType.id}`, response);
      })
      .catch(() => {
        throw new Error('Failed to save competitionType to sync queue', {
          cause: 'Failed to open cache',
        });
      });
  }
}

const removeCompetitionTypeFromSyncQueue = async (competitionType: Syncable<CompetitionType>) => {
  const currentDraftCache = caches.open('draftPlannerCompetitionTypes');
  currentDraftCache
    .then((cache) => {
      cache.delete(`competitionType-${competitionType.id}`);
    })
    .catch(() => {
      throw new Error('Failed to remove competitionType from sync queue', {
        cause: 'Failed to open cache',
      });
    });
};

async function getUnsyncedCompetitionTypes() {
  let competitionTypes: Syncable<CompetitionType>[] = [];
  const currentDraftCache = await caches.open('draftPlannerCompetitionTypes');
  try {
    const keys = await currentDraftCache.keys();
    const promises = keys.map(async (key) => {
      const response = await currentDraftCache.match(key);
      if (response) {
        return response.json();
      }
    });
    const data = await Promise.all(promises);
    competitionTypes = data.filter((p) => p !== null) as Syncable<CompetitionType>[];
  } catch (e) {
    console.error('Failed to get keys from draftPlannerCompetitionTypes cache', {
      cause: e,
    });
  }
  return competitionTypes;
}

// --- CompetitionTypes CRUD ---

export async function saveCompetitionType(
  competitionType: Syncable<CompetitionType>,
  fromSync?: boolean,
): Promise<Syncable<CompetitionType>> {
  if (!$isOnline.get()) {
    if (fromSync) {
      return competitionType;
    }
    await addCompetitionTypeToSyncQueue(competitionType);
    const _competitionTypes = $competitionTypes.get();
    const index = _competitionTypes.findIndex((t) => t.id === competitionType.id);
    if (index !== -1) {
      const updatedCompetitionTypes = [..._competitionTypes];
      updatedCompetitionTypes[index] = competitionType;
      $competitionTypes.set(updatedCompetitionTypes);
    } else {
      $competitionTypes.set([..._competitionTypes, competitionType]);
    }
    return competitionType;
  }
  const { isDraft, synced, ...payload } = competitionType;

  if (competitionType.isDraft) {
    const { data, errors } = await client.models.CompetitionType.create(payload);
    if (data && data.id === competitionType.id) {
      competitionType.synced = true;
      competitionType.isDraft = false;
      void removeCompetitionTypeFromSyncQueue(competitionType);
    }

    if (errors && errors.length > 0 && !fromSync) {
      await addCompetitionTypeToSyncQueue(competitionType);
    }
    const _competitionTypes = $competitionTypes.get();
    const index = _competitionTypes.findIndex((t) => t.id === competitionType.id);
    if (index !== -1) {
      const updatedCompetitionTypes = [..._competitionTypes];
      updatedCompetitionTypes[index] = competitionType;
      $competitionTypes.set(updatedCompetitionTypes);
    } else {
      $competitionTypes.set([..._competitionTypes, competitionType]);
    }
  } else {
    const { data, errors } = await client.models.CompetitionType.update({
      ...payload,
      id: competitionType.id!,
    });
    if (data && data.id === competitionType.id) {
      competitionType.synced = true;
      void removeCompetitionTypeFromSyncQueue(competitionType);
    }
    if (errors && errors.length > 0 && !fromSync) {
      await addCompetitionTypeToSyncQueue(competitionType);
    }
    const _competitionTypes = $competitionTypes.get();
    const index = _competitionTypes.findIndex((t) => t.id === competitionType.id);
    if (index !== -1) {
      const updatedCompetitionTypes = [..._competitionTypes];
      updatedCompetitionTypes[index] = competitionType;
      $competitionTypes.set(updatedCompetitionTypes);
    }
  }

  return competitionType;
}

export async function removeCompetitionType(id: string) {
  if ($isOnline.get()) {
    await client.models.CompetitionType.delete({ id });
  }
  $competitionTypes.set($competitionTypes.get().filter((t) => t.id !== id));
}

export function addCompetitionType(competitionType: CompetitionType) {
  void saveCompetitionType({ ...competitionType, isDraft: true });
}

export function updateCompetitionType(updatedCompetitionType: CompetitionType) {
  void saveCompetitionType(updatedCompetitionType);
}

// --- CompetitionTypes fetch + merge ---

export async function getCompetitionTypes() {
  $competitionTypesIsLoading.set(true);
  const competitionTypes = await fetchCompetitionTypes();
  const unsyncedCompetitionTypes = await getUnsyncedCompetitionTypes();
  const filteredCompetitionTypes = competitionTypes.filter(
    (t) => !unsyncedCompetitionTypes.some((ut) => ut.id === t.id),
  );
  $competitionTypes.set([...filteredCompetitionTypes, ...unsyncedCompetitionTypes]);
  $competitionTypesIsLoading.set(false);
  return [...filteredCompetitionTypes, ...unsyncedCompetitionTypes];
}

// --- CompetitionTypes sync ---

export async function syncPlannerCompetitionTypes() {
  try {
    const draftCache = await getUnsyncedCompetitionTypes();
    if (draftCache.length > 0) {
      console.log('syncPlannerCompetitionTypes', 'draftCache:', draftCache);
      await Promise.all(
        draftCache.map(async (competitionType) => {
          return saveCompetitionType(competitionType, true);
        }),
      );
    }
  } catch (e) {}
}
